import React, { useState, useMemo, useRef, useCallback } from "react";
import {
  DndContext, PointerSensor, useSensor, useSensors, DragOverlay, closestCorners,
} from "@dnd-kit/core";
import {
  SortableContext, horizontalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { motion } from "framer-motion";
import KanbanColumn from "./KanbanColumn";
import KanbanCard from "./KanbanCard";
import LossModal from "./LossModal";
import NewColumnButton from "./NewColumnButton";
import api from "../../lib/api";
import { toast } from "sonner";

function SortableColumn({ column, leads, onCardClick, onColumnUpdate, onColumnDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `col-${column.id}`,
    data: { type: "column", column },
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <KanbanColumn
        column={column}
        leads={leads}
        onCardClick={onCardClick}
        onColumnUpdate={onColumnUpdate}
        onColumnDelete={onColumnDelete}
        dragHandle={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

export default function KanbanBoard({
  columns, leads, onCardClick, onLeadUpdate, onLeadMoveOptimistic, onColumnsChange, onColumnCreate, hiddenColumnIds,
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [activeLead, setActiveLead] = useState(null);
  const [activeColumn, setActiveColumn] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  const [lossOpen, setLossOpen] = useState(false);
  const scrollRef = useRef(null);

  // `columns` continua completo (índice usado pelo reorder via drag). Só
  // filtramos o que é efetivamente renderizado -- ex: Ganho/Perdido ocultos
  // por padrão no Dashboard. Sem hiddenColumnIds, comportamento é idêntico
  // ao de antes.
  const visibleColumns = useMemo(() => {
    if (!hiddenColumnIds || hiddenColumnIds.size === 0) return columns;
    return columns.filter((c) => !hiddenColumnIds.has(c.id));
  }, [columns, hiddenColumnIds]);

  const leadsByCol = useMemo(() => {
    const map = {};
    visibleColumns.forEach((c) => (map[c.id] = []));
    leads.forEach((l) => {
      if (!map[l.column_id]) map[l.column_id] = [];
      map[l.column_id].push(l);
    });
    return map;
  }, [visibleColumns, leads]);

  // ---- Drag-to-scroll (middle-area drag on background) ----
  const dragState = useRef({ isDown: false, startX: 0, scrollLeft: 0 });
  const onMouseDown = (e) => {
    // Only start drag-scroll if clicking empty board area (not card/column/button)
    if (e.target.closest("[data-testid^='kanban-card']")) return;
    if (e.target.closest("[data-column-header]")) return;
    if (e.target.closest("button")) return;
    if (e.target.closest("input,textarea,select,a")) return;
    if (e.button !== 0) return;
    const el = scrollRef.current;
    if (!el) return;
    dragState.current = { isDown: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft };
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";
  };
  const onMouseUp = () => {
    const el = scrollRef.current;
    if (!el) return;
    dragState.current.isDown = false;
    el.style.cursor = "";
    el.style.userSelect = "";
  };
  const onMouseMove = (e) => {
    if (!dragState.current.isDown) return;
    const el = scrollRef.current;
    if (!el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    el.scrollLeft = dragState.current.scrollLeft - (x - dragState.current.startX);
  };

  const handleDragStart = (e) => {
    const data = e.active.data.current;
    if (data?.type === "column") {
      setActiveColumn(data.column);
    } else {
      setActiveLead(data?.lead || null);
    }
  };

  const handleDragEnd = async (e) => {
    const { active, over } = e;
    setActiveLead(null);
    setActiveColumn(null);
    if (!over) return;

    const activeType = active.data.current?.type;
    // ---- Column reorder ----
    if (activeType === "column") {
      const overId = String(over.id).replace(/^col-/, "");
      const activeId = String(active.id).replace(/^col-/, "");
      if (overId === activeId) return;
      const oldIdx = columns.findIndex((c) => c.id === activeId);
      const newIdx = columns.findIndex((c) => c.id === overId);
      if (oldIdx < 0 || newIdx < 0) return;
      const reordered = arrayMove(columns, oldIdx, newIdx);
      onColumnsChange?.(reordered);
      try {
        await api.patch("/columns/reorder", { order: reordered.map((c) => c.id) });
      } catch {
        toast.error("Erro ao reordenar colunas");
      }
      return;
    }

    // ---- Lead moved ----
    const lead = active.data.current?.lead;
    const toCol = columns.find((c) => c.id === over.id);
    if (!lead || !toCol || lead.column_id === toCol.id) return;

    if (toCol.is_lost) {
      setPendingMove({ lead, column: toCol });
      setLossOpen(true);
      return;
    }

    // Atualização otimista: o card já aparece na coluna nova assim que o
    // usuário solta, sem esperar o PATCH. O POST roda em segundo plano; se
    // falhar, reaplicamos a mesma função com origem/destino trocados pra
    // reverter o card e mostramos um toast de erro.
    const fromColumnId = lead.column_id;
    onLeadMoveOptimistic?.(lead.id, toCol.id, fromColumnId);
    try {
      const { data } = await api.post(`/leads/${lead.id}/move`, { column_id: toCol.id });
      onLeadUpdate?.(data);
      toast.success(`${lead.name} → ${toCol.name}`, {
        description: toCol.is_won ? "Parabéns! 🎉" : undefined,
      });
    } catch {
      onLeadMoveOptimistic?.(lead.id, fromColumnId, toCol.id);
      toast.error("Erro ao mover lead");
    }
  };

  const confirmLoss = async (reason) => {
    if (!pendingMove) return;
    const { lead, column: toCol } = pendingMove;
    const fromColumnId = lead.column_id;
    onLeadMoveOptimistic?.(lead.id, toCol.id, fromColumnId);
    try {
      const { data } = await api.post(`/leads/${lead.id}/move`, {
        column_id: toCol.id, loss_reason: reason,
      });
      onLeadUpdate?.(data);
      toast.info(`${lead.name} marcado como perdido`);
    } catch {
      onLeadMoveOptimistic?.(lead.id, fromColumnId, toCol.id);
      toast.error("Erro ao registrar perda");
    }
    setPendingMove(null);
    setLossOpen(false);
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div
          ref={scrollRef}
          data-testid="kanban-board"
          className="flex gap-4 overflow-x-auto pb-6 pr-6 h-full items-start select-none cursor-grab"
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onMouseMove={onMouseMove}
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <style>{`[data-testid=kanban-board]::-webkit-scrollbar{display:none}`}</style>
          <SortableContext
            items={visibleColumns.map((c) => `col-${c.id}`)}
            strategy={horizontalListSortingStrategy}
          >
            {visibleColumns.map((col) => (
              <SortableColumn
                key={col.id}
                column={col}
                leads={leadsByCol[col.id] || []}
                onCardClick={onCardClick}
                onColumnUpdate={(id, patch) => onColumnsChange?.(
                  columns.map((c) => c.id === id ? { ...c, ...patch } : c)
                )}
                onColumnDelete={(id) => onColumnsChange?.(columns.filter((c) => c.id !== id))}
              />
            ))}
          </SortableContext>
          <NewColumnButton onCreate={onColumnCreate} />
        </div>

        <DragOverlay>
          {activeLead ? (
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1.05 }} style={{ width: 304 }}>
              <KanbanCard lead={activeLead} index={0} onClick={() => {}} />
            </motion.div>
          ) : activeColumn ? (
            <div style={{ opacity: 0.9, width: 320 }}>
              <div className="bg-slate-800 border border-slate-600 rounded-xl p-3 shadow-2xl">
                <div className="text-sm font-bold text-slate-100">{activeColumn.name}</div>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <LossModal
        open={lossOpen}
        onCancel={() => { setLossOpen(false); setPendingMove(null); }}
        onConfirm={confirmLoss}
        leadName={pendingMove?.lead?.name}
      />
    </>
  );
}
